import MentionMediaThumbnail from '@/components/mentions/MentionMediaThumbnail';
import { toaster } from '@/components/ui/toaster';
import { useMentionQuery } from '@/hooks/queries/use-mention-query';
import { Box, Skeleton, Text } from '@chakra-ui/react';
import type { MessageData } from '@fluenceur/types';
import { MentionPlatform, MessageTypeEnum } from '@fluenceur/types';
import { useState } from 'react';
import MessageContainer from './MessageContainer';

interface MentionMessageProps {
  message: MessageData;
  time: string;
}

const MentionMessage: React.FC<MentionMessageProps> = ({ message, time }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Fetch the real mention data
  const {
    data: mention,
    isLoading: isMentionLoading,
    isError: isMentionError,
  } = useMentionQuery({
    mentionId: message.mentionId,
    enabled: !!message.mentionId,
  });

  const handleMediaError = (_error: Error) => {
    toaster.error({
      title: 'Error',
      description: 'Could not load mention media',
    });
  };

  const getMentionText = () => {
    const platform = mention?.platform === MentionPlatform.TIKTOK ? 'TikTok' : 'Instagram';
    if (message.type === MessageTypeEnum.STORY_MENTION) {
      return `Mentioned you in a story on ${platform}`;
    }
    return `Mentioned you in a post on ${platform}`;
  };

  const renderMentionContent = () => {
    if (isMentionLoading) {
      return (
        <Box mb={2}>
          <Skeleton height="200px" width="100%" rounded="md" />
        </Box>
      );
    }

    if (isMentionError || !mention) {
      return (
        <Box
          mb={2}
          p={3}
          bg="gray.50"
          rounded="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="gray.500" fontSize="sm">
            Unable to load mention content
          </Text>
        </Box>
      );
    }

    return (
      <Box
        position="relative"
        overflow="hidden"
        mb={2}
        aspectRatio={2 / 3}
        width="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <MentionMediaThumbnail
          mention={mention}
          enabled={!!message.mentionId}
          isHovered={isHovered}
          onError={handleMediaError}
        />
      </Box>
    );
  };

  return (
    <MessageContainer time={time} bg="gray.100" p={3} rounded="lg" isSelf={false}>
      <Text fontSize="15px" mb={2}>
        {getMentionText()}
      </Text>

      {message.mentionId && renderMentionContent()}

      {message.content && <Text color="gray.700">{message.content}</Text>}
    </MessageContainer>
  );
};

export default MentionMessage;
